/**
 * Heap transport wrapper.
 *
 * This module is the SINGLE place client code touches `window.heap`. It is
 * completely inert ("no-op everywhere") when `config.heapAppId` is unset — which
 * is the default in every environment — so importing or calling anything here is
 * always safe.
 *
 * Privacy is enforced **by construction**:
 *  - Every outbound payload passes through the allow-list (`pickAllowed`) before
 *    it can reach Heap.
 *  - The shared segmentation base-prop block is registered ONCE via
 *    `addEventProperties` (`setEventBaseProps`) and rides on every event AND
 *    native pageview automatically.
 *  - A dev-only self-assert (`assertAllowed`) throws/logs if a payload would
 *    carry a key outside its allow-list — defense in depth behind the by-design
 *    filtering.
 *  - `track()` (custom events) is hard-gated to no-op until `identify()` +
 *    `addUserProperties()` have run, structurally enforcing "no custom event
 *    tracking before login completes". `trackPageview()` is NOT gated:
 *    anonymous pre-login pageviews are captured for funnel analysis (they carry
 *    no base props until identify runs).
 *
 * The transport is SWAPPABLE: every operation funnels through a thin internal
 * `send()` seam (`window.heap?.*`). A future change to a different ingestion
 * route (e.g. a GTM container) is localized to `send()` and the call sites here;
 * `events.ts` and every call site stay untouched. We do NOT add that route now.
 */

import { config } from '@/config'
import {
  buildBaseProps,
  pickAllowed,
  IDENTITY_KEYS,
  isEmailShaped,
  isUuid,
  type IdentityProps,
} from './allowlist'

/**
 * Allow-list keys for the custom properties attached to a native pageview. The
 * route TEMPLATE rides as the pageview's built-in `url`/`Path` (PII-safe, never a
 * resolved path); these are the additional properties:
 *  - `route_id`: the file-route id, for warehouse granularity.
 *  - `property_id` / `buybox_id`: the resolved entity id on an entity-detail
 *    route, so per-entity pageview analysis (views, dwell time) works without
 *    fragmenting the Pages report's Path dimension. Opaque entity ids the actor
 *    owns/views — never a user id or any resolved URL. Drop-unless-listed: a key
 *    not in this set never reaches Heap on a pageview.
 */
const PAGEVIEW_PROP_KEYS = ['route_id', 'property_id', 'buybox_id'] as const

/** Entity-id properties that may ride on a native pageview (drop-unless-listed). */
export type PageviewEntityProps = Partial<Record<'property_id' | 'buybox_id', string>>

/** Whether we are in a dev/test build (Vite's `import.meta.env.DEV`). */
function isDevBuild(): boolean {
  try {
    return Boolean(import.meta.env?.DEV)
  } catch {
    return false
  }
}

/**
 * Module-private identify state. Drives the no-track-before-identify gate and
 * provides the base-prop snapshot stamped onto every event.
 *
 * `identified` flips true only after a successful `identify()` and stays true
 * across `addUserProperties` updates; `reset()` clears it (logout / cross-tab).
 */
let identified = false
let currentIdentity: IdentityProps | null = null

/** Is Heap configured at all? When false, EVERYTHING in this module no-ops. */
function isEnabled(): boolean {
  return Boolean(config.heapAppId)
}

/**
 * The swappable transport seam. Calls `window.heap?.<method>` defensively: Heap
 * may not have finished loading (the loader installs a stub queue, but we still
 * guard), so this never throws if the method is missing. Swapping ingestion
 * routes (e.g. to a GTM container) changes only this function.
 */
function send(
  method:
    | 'identify'
    | 'addUserProperties'
    | 'track'
    | 'trackPageview'
    | 'setPath'
    | 'addEventProperties'
    | 'clearEventProperties'
    | 'addPageviewProperties'
    | 'resetIdentity',
  ...args: unknown[]
): void {
  const heap = window.heap
  const fn = heap?.[method]
  if (typeof fn === 'function') {
    try {
      const callable = fn as (...a: unknown[]) => void
      callable.apply(heap, args)
    } catch (err) {
      // Never let an analytics transport error surface to the user.
      console.error(`[analytics] window.heap.${method} threw`, err)
    }
  }
}

/**
 * Dev-only defense-in-depth self-assert: every key in `payload` MUST be in
 * `allowedKeys`. `pickAllowed` is the primary by-construction control; this is
 * the belt-and-suspenders check behind it.
 *
 * Critically, callers run this on the FINAL payload — after base props have been
 * stamped — against the FULL allowed set (event keys ∪ base-prop keys). Running
 * it on the pre-merge event props would not exercise the base-prop block at all,
 * so a base prop accidentally introduced outside the allow-list could slip past.
 * Fail loud in dev, log in prod (never break a user session over a payload).
 */
function assertAllowed(context: string, payload: Record<string, unknown>, allowedKeys: readonly string[]): void {
  const allowed = new Set(allowedKeys)
  const leaked = Object.keys(payload).filter((key) => !allowed.has(key))
  if (leaked.length === 0) {
    return
  }
  const message = `[analytics] ${context}: payload carried non-allow-listed keys: ${leaked.join(', ')}`
  if (isDevBuild()) {
    throw new Error(message)
  }
  console.error(message)
}

/**
 * Identify the current user to Heap. The value MUST be the FusionAuth `sub`
 * UUID (the canonical Orchard Offers user ID). A non-UUID value is rejected
 * (never sent) so malformed identity cannot reach Heap.
 *
 * Flips the no-track-before-identify gate open. No-ops when Heap is unconfigured.
 */
export function identify(userId: string): void {
  if (!isEnabled()) {
    return
  }
  if (!isUuid(userId)) {
    console.error('[analytics] identify() rejected: value is not a UUID-shaped user id')
    return
  }
  identified = true
  send('identify', userId)
}

/**
 * Send the allow-listed identity user properties to Heap (segmentation + the
 * `email` warehouse-join bridge). The payload is filtered to `IDENTITY_KEYS` and
 * self-asserted before transport.
 *
 * `email` is the Redshift join key (the dbt model joins Heap↔Orchard on
 * `lower(trim(email))`), so a malformed value would pollute the join. We guard
 * its SHAPE with {@link isEmailShaped}: if `email` is present but malformed we DROP
 * just that key (keeping the other identity props, which are still useful for
 * segmentation) and dev-warn. We do not block the whole identity payload.
 *
 * Caches the identity snapshot used to stamp base props on subsequent events.
 * No-ops when Heap is unconfigured.
 */
export function addUserProperties(props: IdentityProps): void {
  if (!isEnabled()) {
    return
  }
  currentIdentity = props
  const safe = pickAllowed(props as unknown as Record<string, unknown>, IDENTITY_KEYS)
  // Guard the email join key: drop it (only it) if present-but-malformed.
  if ('email' in safe && !isEmailShaped(safe.email)) {
    if (isDevBuild()) {
      console.warn('[analytics] addUserProperties: dropping malformed email (not sent to Heap)')
    }
    delete safe.email
  }
  assertAllowed('addUserProperties', safe, IDENTITY_KEYS)
  send('addUserProperties', safe)
}

/**
 * Track a custom client event. Filters `props` to the per-event allow-list,
 * self-asserts, and sends. Base props are not part of this payload — Heap
 * attaches them globally from the {@link setEventBaseProps} registration.
 *
 * Hard-gated: no-ops when Heap is unconfigured OR before
 * `identify()` + `addUserProperties()` have run (the `currentIdentity` snapshot
 * doubles as the "Phase 2 ran, so base props are registered" signal — gating on
 * it guarantees a custom event never fires base-prop-less). This is the
 * structural "no custom tracking before login" guarantee — call sites do not
 * need to check.
 *
 * Called only by `events.ts` (the typed firing idiom); never call this directly
 * from feature code.
 */
export function trackEvent(
  eventName: string,
  props: Record<string, unknown>,
  allowedKeys: readonly string[]
): void {
  if (!isEnabled() || !identified || !currentIdentity) {
    return
  }
  const safeEventProps = pickAllowed(props, allowedKeys)
  // Self-assert the event's own props against its allow-list. `pickAllowed`
  // above is the primary by-construction control; this is defense-in-depth.
  assertAllowed(`track:${eventName}`, safeEventProps, allowedKeys)
  send('track', eventName, safeEventProps)
}

/**
 * Register the shared segmentation base-prop block on Heap as EVENT PROPERTIES
 * (`addEventProperties`). Once set, these ride on every subsequent custom event
 * AND on native pageviews. Re-call on a profile change (e.g. onboarding
 * completion) to overwrite the same keys with current values; `buildBaseProps`
 * always returns the full key set, so no stale key lingers. No-ops when Heap is
 * unconfigured.
 *
 * Called from Phase 2 of the identify lifecycle, right after `addUserProperties`
 * and BEFORE the first event (Sign-In Completed) so that event carries them.
 */
export function setEventBaseProps(identity: IdentityProps): void {
  if (!isEnabled()) {
    return
  }
  // `sentryEnvironment` is `''` (not undefined) when VITE_SENTRY_ENVIRONMENT is
  // unset; default to a non-empty sentinel so we never ship an empty-string
  // `app_environment` (which would create a silent empty bucket in Redshift).
  const appEnvironment = config.sentryEnvironment || 'unknown'
  send('addEventProperties', buildBaseProps(identity, appEnvironment))
}

/**
 * Fire a NATIVE Heap pageview for a resolved route. Receives the route TEMPLATE
 * (e.g. `/properties/$id`) and route id — never a resolved URL. The template is
 * sent as the pageview's `url`, so Heap's built-in `Path` (which feeds the Pages
 * report + time-on-page) is populated with the PII-safe template; the route id
 * and any `entityProps` (e.g. `property_id`) ride as custom properties for
 * warehouse granularity + per-entity analysis. Base props attach automatically
 * via the `addEventProperties` registration.
 *
 * `entityProps` is allow-list-filtered to `PAGEVIEW_PROP_KEYS`, so only opaque
 * entity ids can ride — anything else is dropped before transport.
 *
 * NOT gated on identify — anonymous pre-login pageviews are captured for funnel
 * analysis (e.g. the request-account flow). Before identify they carry no base
 * props; Heap associates them with the user once `identify()` runs. No-ops only
 * when Heap is unconfigured. (Custom events via `trackEvent` stay gated.)
 */
export function trackPageview(path: string, routeId: string, entityProps: PageviewEntityProps = {}): void {
  if (!isEnabled()) {
    return
  }
  send('trackPageview', {
    url: path,
    properties: pickAllowed({ route_id: routeId, ...entityProps }, PAGEVIEW_PROP_KEYS),
  })
}

/**
 * Attach entity-id properties (e.g. `property_id`) to the CURRENT pageview via
 * `addPageviewProperties`. This is the lever for the unavoidable initial
 * autocapture pageview (the one `trackPageview` does NOT fire, to avoid a
 * duplicate): it enriches that pageview so a direct deep-link into an entity
 * page — e.g. a digest-email link to `/properties/<id>` — still carries the
 * entity id for per-entity analysis.
 *
 * Allow-list-filtered to `PAGEVIEW_PROP_KEYS` and no-ops when there is nothing
 * safe to attach. Carries only opaque entity ids (PII-safe), so — like
 * `setPath` — it is intentionally NOT gated on identify (the autocapture
 * pageview it enriches fires pre-identify). No-ops when Heap is unconfigured.
 */
export function setPageviewEntityProps(entityProps: PageviewEntityProps): void {
  if (!isEnabled()) {
    return
  }
  const safe = pickAllowed(entityProps, PAGEVIEW_PROP_KEYS)
  if (Object.keys(safe).length === 0) {
    return
  }
  send('addPageviewProperties', safe)
}

/**
 * Pin the path Heap reports for the UNAVOIDABLE autocapture pageview. Heap fires
 * one pageview on every hard page load (and on session-restart) from the
 * resolved `window.location` — `disablePageviewAutocapture` does NOT suppress it.
 * Pointing Heap at the route TEMPLATE first makes that pageview report the
 * template (e.g. `/properties/$id`) instead of a resolved URL, keeping the Pages
 * report PII-safe and consistent with our manual SPA pageviews.
 *
 * Carries only the route template (PII-safe), so — unlike `track`/`trackPageview`
 * — it is intentionally NOT gated on identify: the autocapture pageview it
 * influences fires pre-identify, and we still want it templated. No-ops when
 * Heap is unconfigured.
 */
export function setPath(path: string): void {
  if (!isEnabled()) {
    return
  }
  send('setPath', path)
}

/**
 * Reset Heap identity (logout / cross-tab session clear) so a shared browser
 * does not bleed sessions. Closes the no-track gate. No-ops when unconfigured.
 */
export function reset(): void {
  identified = false
  currentIdentity = null
  if (!isEnabled()) {
    return
  }
  // Clear the registered event base props (set via `addEventProperties`) BEFORE
  // resetting identity, so a shared browser does not bleed one user's
  // segmentation onto the next user's events.
  send('clearEventProperties')
  send('resetIdentity')
}

/**
 * Test-only: reset module-private identify state between tests. Not exported
 * from the package index; intended for use by this module's own test suite.
 */
export function __resetForTests(): void {
  identified = false
  currentIdentity = null
}
